import React, { Component } from "react";
import { Modal } from "reactstrap";
import {
  listroomBookingCancellationRequest,
  approveCancellationRequest,
  disapproveCancellationRequest,
} from "../../Endpoint/Admin/index";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C, Panel, StatCard, StatRow, Table, Chip, Btn, Pager,
} from "../../components/Console/ConsoleUI";
import PeriodFilter, { allTime, periodToParams, periodLabel } from "../../components/Common/PeriodFilter";
import { toast } from "react-toastify";
import { canWrite } from "../../services/PermissionService";

/**
 * Room cancellation requests — console screen G1·26 ("Dialog · Cancel booking").
 *
 * Approving a cancellation frees the room and refunds the guest, so it now goes
 * through the design's confirmation dialog rather than firing on a single
 * click. The dialog calls exactly the same approveCancellationRequest it always
 * did; declining simply closes it.
 */

const initials = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0))
    .join("")
    .toUpperCase() || "?";

/** The API returns raw ISO strings here; show them as a person would write them. */
const day = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

class RoomCancel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      page: 1,
      limit: 25,
      pagination: null,
      period: allTime(),
      items: [],
      isLoaded: false,
      // The row whose cancellation is being confirmed, if any.
      confirming: null,
      breadcrumbItems: [
        { title: "Dashboard", link: "#" },
        { title: "Room Cancellation Requests", link: "#" },
      ],
      tableData: {
        columns: [
          { label: "Room Name", field: "roomName", sort: "asc", width: 60 },
          { label: "Order Date", field: "createdAt", sort: "asc", width: 60 },
          {
            label: "Customer Name",
            field: "guestName",
            sort: "asc",
            width: 60,
          },
          {
            label: "Customer Email",
            field: "guestEmail",
            sort: "asc",
            width: 60,
          },
          {
            label: "Customer Mobile",
            field: "guestMobile",
            sort: "asc",
            width: 60,
          },
          { label: "Start Date", field: "startDate", sort: "asc", width: 60 },
          { label: "End Date", field: "endDate", sort: "asc", width: 60 },
          {
            label: "Cancellation Reason",
            field: "cancellationReason",
            sort: "asc",
            width: 60,
          },
          { label: "Status", field: "status", sort: "asc", width: 100 },
          { label: "Approve", field: "approve", sort: "asc", width: 100 },
          { label: "Disapprove", field: "disapprove", sort: "asc", width: 100 },
        ],
        rows: [],
      },
    };
    this.approveRequest = this.approveRequest.bind(this);
    this.disapproveRequest = this.disapproveRequest.bind(this);
  }

  componentDidMount() {
    this.getAllRequests();
  }

  approveRequest(roomBookingId) {
    approveCancellationRequest(roomBookingId)
      .then((response) => {
        if (response.status !== 200) {
          toast.error(response.message, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        } else {
          toast.success(response.message, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        }
        this.getAllRequests();
      })
      .catch((error) => {
        toast.error("Error approving request");
      });
  }

  disapproveRequest(roomBookingId) {
    disapproveCancellationRequest(roomBookingId)
      .then((response) => {
        if (response.status !== 200) {
          toast.error(response.message, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        } else {
          toast.success(response.message, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        }
        this.getAllRequests();
      })
      .catch((error) => {
        toast.error("Error disapproving request");
      });
  }

  handlePeriodChange = (period) => {
    this.setState({ period }, () => this.getAllRequests(1, this.state.limit));
  };

  getAllRequests(page = this.state.page, limit = this.state.limit) {
    listroomBookingCancellationRequest(page, limit, periodToParams(this.state.period)).then((result) => {
      if (result.status === 200) {
        const notifications = result.data.map((x) => {
          const status = x.cancelled ? "Approved" : "Not Approved";
          let approveButton = null;
          let disapproveButton = null;

          // Deciding a cancellation is a write. A view-only account still sees
          // the request and its status, just no button to act on it.
          if (!canWrite()) {
            // leave both null
          } else if (status === "Approved") {
            disapproveButton = (
              <button
                type="button"
                style={{
                  height: 32, padding: "0 14px", borderRadius: 16, fontSize: 13,
                  fontFamily: "inherit", cursor: "pointer", border: "none",
                  background: C.red, color: "#fff",
                }}
                onClick={() => this.disapproveRequest(x._id.toString())}
              >
                Not Approve
              </button>
            );
          } else {
            approveButton = (
              <button
                type="button"
                style={{
                  height: 32, padding: "0 14px", borderRadius: 16, fontSize: 13,
                  fontFamily: "inherit", cursor: "pointer",
                  border: `1px solid ${C.border}`, background: "#fff", color: C.brown,
                }}
                onClick={() => this.setState({ confirming: x })}
              >
                Approve
              </button>
            );
          }

          return {
            ...x,
            status,
            approve: approveButton,
            disapprove: disapproveButton,
          };
        });

        this.setState({
          isLoaded: true,
          items: result.data,
          pagination: result.pagination || null,
          page,
          limit,
          tableData: {
            ...this.state.tableData,
            rows: notifications,
          },
        });
      } else {
        toast.error("Error fetching requests");
      }
    });
  }

  /** Console columns, driven by the same field list the old data table used. */
  consoleColumns() {
    return [
      {
        key: "guestName",
        label: "Guest",
        render: (x) => (
          <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
            <span
              style={{
                width: 34, height: 34, borderRadius: 17, flex: "0 0 34px",
                background: C.tint, color: C.brown, display: "grid", placeItems: "center",
                fontSize: 13, fontWeight: 700,
              }}
            >
              {initials(x.guestName)}
            </span>
            <div>
              <div style={{ fontSize: 15, color: C.ink }}>{x.guestName || "N/A"}</div>
              <div style={{ fontSize: 12, color: C.taupeLight }}>
                {x.guestMobile || x.guestEmail || "—"}
              </div>
            </div>
          </div>
        ),
      },
      {
        key: "roomName",
        label: "Room",
        render: (x) => <span style={{ color: C.ink }}>{x.roomName || "—"}</span>,
      },
      {
        key: "stay",
        label: "Stay",
        render: (x) => (
          <span style={{ color: C.taupe }}>
            {day(x.startDate)} → {day(x.endDate)}
          </span>
        ),
      },
      {
        key: "createdAt",
        label: "Requested",
        render: (x) => <span style={{ color: C.taupe }}>{day(x.createdAt)}</span>,
      },
      {
        key: "cancellationReason",
        label: "Reason",
        render: (x) => (
          <span style={{ color: C.slate }}>{x.cancellationReason || "—"}</span>
        ),
      },
      {
        key: "status",
        label: "Status",
        render: (x) =>
          x.status === "Approved" ? (
            <Chip tone="bad">Cancelled</Chip>
          ) : (
            <Chip tone="warn">Awaiting review</Chip>
          ),
      },
      ...(canWrite()
        ? [
            {
              key: "actions",
              label: "",
              render: (x) => (
                <div style={{ display: "flex", gap: 8 }}>
                  {x.approve}
                  {x.disapprove}
                </div>
              ),
            },
          ]
        : []),
    ];
  }

  /** Screen G1·26 — the confirmation the design puts in front of a refund. */
  renderConfirmDialog() {
    const b = this.state.confirming;
    const close = () => this.setState({ confirming: null });

    return (
      <Modal
        isOpen={!!b}
        toggle={close}
        contentClassName="bg-transparent border-0"
          className="con-modal"
        >
        {b && (
          <div style={{ background: "#fdf9f3", borderRadius: 22, padding: 32 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
              <span
                className="ms"
                style={{ fontSize: 26, color: C.red, background: "rgba(192,32,43,.10)", padding: 13, borderRadius: 16 }}
              >
                report
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 22, fontWeight: 500, color: C.ink, letterSpacing: "-.3px" }}>
                  Cancel this booking?
                </div>
                <div style={{ marginTop: 8, fontSize: 15, color: C.slate, lineHeight: 1.55 }}>
                  {b.roomName || "The room"} · {b.guestName || "the guest"} ·{" "}
                  {day(b.startDate)} – {day(b.endDate)}. The room returns to
                  inventory immediately and the guest is notified in the app.
                </div>
              </div>
              <span
                className="ms"
                onClick={close}
                style={{ fontSize: 22, color: C.taupeLight, cursor: "pointer" }}
              >
                close
              </span>
            </div>

            <div style={{ marginTop: 22, padding: "16px 18px", borderRadius: 14, background: C.iconTint }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: C.slate }}>
                <span>Guest contact</span>
                <span style={{ color: C.ink }}>{b.guestMobile || b.guestEmail || "—"}</span>
              </div>
              <div style={{ marginTop: 9, display: "flex", justifyContent: "space-between", fontSize: 14, color: C.slate }}>
                <span>Requested on</span>
                <span style={{ color: C.ink }}>{day(b.createdAt)}</span>
              </div>
              <div
                style={{
                  marginTop: 12, paddingTop: 12, borderTop: "1px solid #e6d8c4",
                  display: "flex", justifyContent: "space-between",
                  fontSize: 16, fontWeight: 500, color: C.ink,
                }}
              >
                <span>Payment</span>
                <span>{b.isPaymentSuccess ? "Paid — refund due" : "Not paid"}</span>
              </div>
            </div>

            <div style={{ marginTop: 18 }}>
              <div style={{ fontSize: 13, color: C.taupe, marginBottom: 7, letterSpacing: ".6px" }}>
                REASON GIVEN BY THE GUEST
              </div>
              <div
                style={{
                  minHeight: 50, padding: "13px 15px", background: "#fff",
                  border: "1px solid #e6d8c4", borderRadius: 12,
                  fontSize: 15, color: C.ink,
                }}
              >
                {b.cancellationReason || "No reason given"}
              </div>
            </div>

            <div style={{ marginTop: 26, display: "flex", gap: 12 }}>
              <Btn kind="quiet" onClick={close} style={{ flex: 1, height: 52, borderRadius: 26, fontSize: 15 }}>
                Keep booking
              </Btn>
              <Btn
                kind="danger"
                style={{ flex: 1, height: 52, borderRadius: 26, fontSize: 15 }}
                onClick={() => {
                  this.setState({ confirming: null });
                  this.approveRequest(b._id.toString());
                }}
              >
                Cancel &amp; refund
              </Btn>
            </div>
          </div>
        )}
      </Modal>
    );
  }

  render() {
    // The list endpoint answers 400 when there is nothing to review, so
    // `isLoaded` can never become true on an empty tenant. Empty is therefore
    // reported as empty rather than as a permanent "loading".
    const { tableData } = this.state;
    const rows = tableData.rows || [];
    const approved = rows.filter((x) => x.status === "Approved").length;
    const paidPending = rows.filter((x) => x.status !== "Approved" && x.isPaymentSuccess).length;

    return (
      <ConsolePage
        title="Cancellation requests"
        subtitle={(() => {
          // rows is ONE PAGE. Using its length as the total read as
          // "25 requests" regardless of how many there actually are.
          const t = (this.state.pagination && this.state.pagination.totalCount) || rows.length;
          return t > rows.length
            ? `Rooms · ${t} requests · showing ${rows.length}`
            : `Rooms · ${t} request${t === 1 ? "" : "s"}`;
        })()}
      >
        {this.renderConfirmDialog()}

        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <PeriodFilter value={this.state.period} onChange={this.handlePeriodChange} />
          <StatRow columns={3}>
            {/* The total is server-reported; the two breakdowns can only be
                derived from the loaded page, so they say so. */}
            <StatCard
              icon="assignment_return"
              value={(this.state.pagination && this.state.pagination.totalCount) || rows.length}
              label={this.state.period.mode === "all" ? "Requests received" : `Requests · ${periodLabel(this.state.period)}`}
            />
            <StatCard icon="pending_actions" value={rows.length - approved} label="Awaiting review, on this page" />
            <StatCard icon="currency_exchange" value={paidPending} label="Paid, refund to settle, on this page" />
          </StatRow>

          <Panel style={{ padding: 0 }}>
            <div
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "16px 22px", borderBottom: `1px solid ${C.panel}`,
              }}
            >
              <div style={{ fontSize: 17, fontWeight: 500, color: C.ink }}>
                Cancellation requests
              </div>
              <div style={{ fontSize: 13, color: C.taupeLight }}>
                {approved} approved
              </div>
            </div>
            <div style={{ padding: "0 10px" }}>
              <Table
                columns={this.consoleColumns()}
                rows={rows}
                empty="No cancellation requests"
              />
              {this.state.pagination && (
                <Pager
                  page={this.state.pagination.currentPage}
                  totalPages={this.state.pagination.totalPages}
                  total={this.state.pagination.totalCount}
                  limit={this.state.pagination.limit}
                  onPage={(n) => this.getAllRequests(n, this.state.limit)}
                  onLimit={(n) => this.getAllRequests(1, n)}
                  noun="requests"
                />
              )}
            </div>
          </Panel>
        </div>
      </ConsolePage>
    );
  }
}

export default RoomCancel;
